import { useMemo } from 'react';
import {
  buildWhatsAppUrl, whatsappMessage, formatRupees,
  type HomepageConfig, type Offer, type SiteSettings,
} from '@cng/shared';

interface ComboOffersProps {
  config: HomepageConfig['comboOffers'];
  offers: Offer[];
  settings: SiteSettings;
}

/**
 * Grouped combo offers. Every "Book" button builds its WhatsApp message from a
 * template in site settings, replacing the 13 hand-written messages the
 * original hard-codes into individual buttons.
 */
export function ComboOffers({ config, offers, settings }: ComboOffersProps) {
  const groups = useMemo(() => {
    const configured = (config?.groups ?? []).slice().sort((a, b) => a.order - b.order);
    const byKey = new Map<string, Offer[]>();
    for (const offer of offers) {
      const key = offer.group || 'other';
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(offer);
    }
    for (const list of byKey.values()) list.sort((a, b) => a.order - b.order);

    const known = configured
      .map((g) => ({ ...g, offers: byKey.get(g.key) ?? [] }))
      .filter((g) => g.offers.length > 0);

    // An offer given a group the homepage config does not know about still shows.
    const knownKeys = new Set(configured.map((g) => g.key));
    const extras = [...byKey.entries()]
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, list]) => ({
        key,
        heading: list[0]?.group || 'More Offers',
        icon: '',
        gstNote: '',
        order: 999,
        offers: list,
      }));

    return [...known, ...extras];
  }, [config?.groups, offers]);

  if (!config?.enabled || groups.length === 0) return null;

  const waPhone = settings.whatsappNumber || settings.primaryPhone;
  const templates = settings.whatsappTemplates;

  return (
    <section className="cg-combo" aria-labelledby="combo-offers-title">
      {config.ghostText ? <span className="cg-ghost" aria-hidden="true">{config.ghostText}</span> : null}

      <div className="cg-wrap">
        <div className="cg-header">
          {config.eyebrow ? (
            <span className="cg-eyebrow">
              <span className="cg-ebar" aria-hidden="true" />
              {config.eyebrow}
            </span>
          ) : null}
          <h2 className="cg-title" id="combo-offers-title">
            {config.title}
            {config.titleAccent ? <> <em>{config.titleAccent}</em></> : null}
          </h2>
          {config.subtitle ? <p className="cg-sub">{config.subtitle}</p> : null}
        </div>

        {groups.map((group) => (
          <div className="cg-category" key={group.key}>
            <div className="cg-cat-heading">
              {group.icon ? <span className="cg-tag-icon" aria-hidden="true">{group.icon}</span> : null}
              <h3>{group.heading}</h3>
            </div>

            <div className="cg-grid">
              {group.offers.map((offer) => {
                const price = offer.displayPriceText || formatRupees(offer.price);
                const message = offer.whatsappMessage
                  || whatsappMessage('offerBooking', { name: offer.title, price: offer.price ?? '' }, templates);
                return (
                  <div className={`cg-card${offer.featured ? ' cg-popular' : ''}`} key={offer._id}>
                    {offer.badge ? <span className="cg-badge">{offer.badge}</span> : null}
                    <div className="cg-card-name">{offer.title}</div>
                    <div className="cg-price-row">
                      {offer.originalPrice ? (
                        <span className="cg-price-was">{formatRupees(offer.originalPrice)}</span>
                      ) : null}
                      <span className="cg-price">{price}</span>
                      <a
                        className="cg-book-btn"
                        href={buildWhatsAppUrl({ phone: waPhone, message })}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Book ${offer.title} on WhatsApp for ${price}`}
                      >
                        {offer.ctaLabel || 'Book'}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {group.gstNote ? <p className="cg-gst-note">{group.gstNote}</p> : null}
          </div>
        ))}

        {config.ctaLabel ? (
          <div className="cg-cta">
            <a
              className="cg-cta-btn"
              href={buildWhatsAppUrl({
                phone: waPhone,
                message: whatsappMessage('comboEnquiry', {}, templates),
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {config.ctaLabel}
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
}
